import { mutate } from 'swr';

export const CART_CACHE_KEY = '/action/cart/getCart';

type CartResponse = { isError?: boolean; data?: unknown } | undefined;
type CartIdentity = { cartId?: string; cartVersion?: string | number };
const identityOf = (res: CartResponse): CartIdentity =>
  res?.data && typeof res.data === 'object' ? (res.data as CartIdentity) : {};

const versionOf = (res: CartResponse): number | undefined => {
  const version = Number(identityOf(res).cartVersion);
  return Number.isFinite(version) ? version : undefined;
};

/**
 * Which cart response the cache should hold: `incoming`, unless it would move the cart BACKWARDS.
 *
 * A commercetools cart's `version` only goes up, so a snapshot of the SAME cart with a LOWER
 * version than the one cached is older than what the shopper already sees. It arrives when a
 * request that read the cart early (a no-op that returns the snapshot it decided on, a slow
 * response) settles after a request that wrote it later, possibly from another tab. It is dropped.
 * An equal version is the same state and is harmless. A response that is not a cart (an error, or
 * a response with no cart in it) never replaces the cached one.
 *
 * Cart IDENTITY is guarded separately: a snapshot of a different cart than the one the client is on
 * (`expectedCartId`) is dropped, however new. The expected id moves only on an explicit cart switch
 * (`publishCart(res, { switchesCart: true })`: loading a saved cart) or an authoritative server
 * read of the session cart (`noteAuthoritativeCart`: the cart fetcher, which every revalidation
 * runs, so the new cart after an order, a reset, a login or a logout arrives that way).
 */
export const newerCartResponse = <T extends CartResponse>(current: T, incoming: T, expectedCartId?: string): T => {
  if (!incoming || incoming.isError || !identityOf(incoming).cartId) return current;
  // A response for a cart the client is no longer on (a late reply for cart A after switching to
  // cart B) is never installed. Only an explicit switch, or an authoritative read, moves the id.
  if (expectedCartId !== undefined && identityOf(incoming).cartId !== expectedCartId) return current;
  if (!current || current.isError || identityOf(current).cartId !== identityOf(incoming).cartId) return incoming;
  const cachedVersion = versionOf(current);
  const incomingVersion = versionOf(incoming);
  if (cachedVersion !== undefined && incomingVersion !== undefined && incomingVersion < cachedVersion) {
    return current;
  }
  return incoming;
};

/**
 * The ONE way a cart response is written into the shared cart cache. Every current writer in
 * `useCart` goes through it. A source test flags a direct `mutate(CART_CACHE_KEY, data)` anywhere in
 * the frontend; it cannot see an aliased mutate, so it is a tripwire, not a guarantee. Revalidations
 * (`mutate(CART_CACHE_KEY)`, a fresh server read) do not install a snapshot and do not need it.
 */
export const publishCart = (res: CartResponse, options: { switchesCart?: boolean } = {}) => {
  const incomingId = identityOf(res).cartId;
  if (options.switchesCart && incomingId && !res?.isError) {
    expectedCartId = incomingId;
    cartGeneration += 1; // any read that started before this switch is now stale
  } else if (incomingId && expectedCartId !== undefined && incomingId !== expectedCartId) {
    // A different cart than expected, not from a switch: the server may have moved the session
    // (e.g. a recreated cart) or the reply is simply late. Either way do not trust the snapshot;
    // re-read the session cart, which is authoritative for identity.
    return mutate(CART_CACHE_KEY);
  }
  return mutate(CART_CACHE_KEY, (current: CartResponse) => newerCartResponse(current, res, expectedCartId), {
    revalidate: false,
  });
};

/** The id of the cart the client is on: set by an authoritative read or an explicit switch only. */
let expectedCartId: string | undefined;

/**
 * Cart generation: incremented by every explicit switch. A server read is authoritative for
 * identity only if no switch happened while it was in flight. A read that STARTED before
 * `loadSavedCart` but lands after it describes the cart the client just left.
 */
let cartGeneration = 0;

/** Record the session cart an authoritative server read returned (a read that began at `startedAt`). */
export const noteAuthoritativeCart = <T extends CartResponse>(res: T, startedAt: number = cartGeneration): T => {
  const id = identityOf(res).cartId;
  if (id && !res?.isError && startedAt === cartGeneration) expectedCartId = id;
  return res;
};

/**
 * The SWR cart fetcher. It returns only a read that began AFTER the latest explicit switch: a read
 * overtaken by a switch is discarded and the cart is read again (bounded; after that it fails, and
 * SWR keeps the cached cart), so neither the cache nor the expected identity can be pointed back at
 * the cart the client switched away from.
 */
export const readAuthoritativeCart = async <T extends CartResponse>(read: () => Promise<T>, attempts = 3): Promise<T> => {
  for (let attempt = 1; ; attempt++) {
    const startedAt = cartGeneration;
    const res = await read();
    if (startedAt === cartGeneration) return noteAuthoritativeCart(res, startedAt);
    // Never hand SWR a read that predates a switch: failing keeps the cache on the switched cart.
    if (attempt >= attempts) throw new Error('The cart kept changing while it was being read.');
  }
};

/** Test seam: forget the expected cart (module state outlives a single test). */
export const resetExpectedCartForTests = () => {
  expectedCartId = undefined;
  cartGeneration = 0;
};
